import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { DEFAULT_PAGE_LOAD_SIZE } from '../../../api/pagination';
import {
    searchClaimQueue,
    searchEligibilityGroups,
    searchParticipants
} from '../../../store/participantService';
import { getNameFromId } from '../../../utils/directusFunctions';
import { CTW_REFERRALS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../utils/sortFunctions';
import { hasRole, PRAP, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import CreateCTWReferralsCsv from './CreateCTWReferralsCsv';
import CTWReferralTableRow from './CTWReferralTableRow';

import app from '../../../app.module.css';

const headCells = [
    { id: 'ptCode', label: 'PT ID' },
    { id: 'uniqueId', label: 'NINO' },
    { id: 'firstName', label: 'First Name' },
    { id: 'middleName', label: 'Middle Name' },
    { id: 'lastName', label: 'Last Name' },
    { id: 'dateOfBirth', label: 'Date of Birth' },
    { id: 'eligibilityGroupId', label: 'Eligibility Group', sortable: false },
    { id: 'address1', label: 'Address line 1' },
    { id: 'address2', label: 'Address line 2' },
    { id: 'city', label: 'Town/City' },
    { id: 'postcode', label: 'Postcode' },
    { id: 'primaryPhoneNumber', label: 'Phone' },
    { id: 'emailAddress', label: 'Email' },
    { id: 'status', label: 'PT Status' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'ptCode',
    page: 0,
    rowsPerPage: 100
};

const generateRoles = [PRAP, QUALITY, SUPERUSER];

const CTWReferral = () => {
    // HOOKS
    const dispatch = useDispatch();
    const { state } = useLocation();

    // LOCAL STATE
    const [participantIds, setParticipantIds] = useState([]);
    const [rows, setRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [isDataSetsModalOpen, setIsDataSetsModalOpen] = useState(false);
    const [generateCSV, setGenerateCSV] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const eligibilityGroupSearch = useSelector(
        (state) => state.entities.participantService.eligibilityGroupSearch
    );
    const participantsByContractAndService = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );
    const claimQueues = useSelector((state) => state.entities.participantService.claimQueues);
    const claimQueuesMetaData = useSelector(
        (state) => state.entities.participantService.claimQueuesMetaData
    );
    const loadingClaimQueuesSearch = useSelector(
        (state) => state.entities.participantService.loadingClaimQueuesSearch
    );

    // USE EFFECTS
    useEffect(() => {
        setRows([]);
    }, []);

    useEffect(() => {
        if (!state) return;
        reset();
        dispatch(
            searchParticipants(
                {
                    contractIds: [state.selects.contract.id],
                    serviceId: state.selects.service.id
                },
                0,
                DEFAULT_PAGE_LOAD_SIZE
            )
        );
    }, [state]);

    useEffect(() => {
        if (
            participantsByContractAndService?.length > 0 &&
            'number' in participantsSearchMetaData &&
            !participantsSearchMetaData.last
        ) {
            dispatch(
                searchParticipants(
                    {
                        contractIds: [state.selects.contract.id],
                        serviceId: state.selects.service.id
                    },
                    participantsSearchMetaData.number + 1,
                    DEFAULT_PAGE_LOAD_SIZE
                )
            );
        } else if (
            participantsSearchMetaData.last &&
            participantsByContractAndService?.length > 0
        ) {
            setParticipantIds(participantsByContractAndService.map((el) => el.id));
            const eligibilityGroupIdsForRows = [
                ...new Set(participantsByContractAndService.map((el) => el.eligibilityGroupId))
            ];
            dispatch(searchEligibilityGroups({ ids: eligibilityGroupIdsForRows }));
        }
    }, [participantsByContractAndService, participantsSearchMetaData]);

    useEffect(() => {
        if (
            participantIds?.length < 1 ||
            !('id' in state.selects.contract) ||
            !('id' in state.selects.service)
        )
            return;

        dispatch(
            searchClaimQueue({
                eventTypeId: state.selects.claimEventType.id,
                statusIds: [state.selects.claimEventStatus.id],
                contractId: state.selects.contract.id,
                serviceId: state.selects.service.id
            })
        );
    }, [participantIds]);

    useEffect(() => {
        if (
            claimQueues.length > 0 &&
            !loadingClaimQueuesSearch &&
            'number' in claimQueuesMetaData &&
            !claimQueuesMetaData.last
        )
            dispatch(
                searchClaimQueue(
                    {
                        eventTypeId: state.selects.claimEventType.id,
                        statusIds: [state.selects.claimEventStatus.id],
                        contractId: state.selects.contract.id,
                        serviceId: state.selects.service.id
                    },
                    claimQueuesMetaData?.number + 1
                )
            );
        else if (claimQueuesMetaData?.last && claimQueues.length > 0) {
            const updatedRows = claimQueues
                .map((el) => {
                    const participant = participantsByContractAndService.find(
                        (entry) => entry.ptCode === el.ptCode
                    );
                    if (participant)
                        return {
                            rowId: el.id,
                            ptCode: participant.ptCode,
                            uniqueId: participant.uniqueIdentifier,
                            firstName: participant.firstName,
                            middleName: participant.middleName,
                            lastName: participant.lastName,
                            dateOfBirth: participant.dateOfBirth || '',
                            eligibilityGroupId: getNameFromId(
                                eligibilityGroupSearch,
                                participant.eligibilityGroupId
                            ),
                            address1: participant?.address1
                                ? participant.address1.replace(/,/g, '')
                                : '',
                            address2: participant?.address2
                                ? participant.address2.replace(/,/g, '')
                                : '',
                            city: participant.city,
                            postcode: participant.postcode,
                            primaryPhoneNumber: participant.primaryPhoneNumber,
                            emailAddress: participant.emailAddress || '',
                            status: participant.status
                        };
                })
                .filter((el) => el);
            setRows(updatedRows);
        } else setRows([]);
    }, [claimQueues, claimQueuesMetaData]);

    // HELPER FNS
    const loadingError = () => {
        if (!state) return 'No Data Set query found for CTW Referrals';
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <CTWReferralTableRow key={el.rowId} row={el} />
        ));

    const reset = () => {
        setRows([]);
        setRowMetaData(initialRowMetaData);
        setGenerateCSV(false);
    };

    // EVENT HANDLERS
    const onDataSetsModalClose = () => {
        setIsDataSetsModalOpen(false);
    };

    const onGenerateCsv = () => {
        setGenerateCSV(true);
        setIsDataSetsModalOpen(true);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={app.pageContainer} data-testid="ctw-start">
            <h1 className={app.mainHeading}>Data Sets Results Screen - CTW Referrals</h1>
            {rows?.length < 1 ? (
                <LoadingSpinner content={CTW_REFERRALS_ERROR} />
            ) : (
                <>
                    <ResultsTable
                        defaultOrderBy={'ptCode'}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />

                    <Button
                        id="generateDataSets"
                        type="button"
                        disabled={!hasRole(generateRoles, roles)}
                        content="Generate"
                        onClick={onGenerateCsv}
                    />
                </>
            )}
            {generateCSV && (
                <CreateCTWReferralsCsv
                    isOpen={isDataSetsModalOpen}
                    onClose={onDataSetsModalClose}
                    rows={rows}
                />
            )}
        </div>
    );
};

export default CTWReferral;
